import { useCallback, useEffect, useState } from 'react';

import type { ExternalCharacterListItem } from '@cartwhl/common/api/getExternalCharacters';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import CartographerCharacterLibrary from '..';

export interface CharacterLibraryLoadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: string | null;
  characters: ExternalCharacterListItem[];
  selectedCharacterId?: string | null;
  charactersLoading: boolean;
  charactersError: string | null;
  loadingCharacterId: string | null;
  onSelectCharacter: (
    character: ExternalCharacterListItem,
  ) => void | Promise<unknown>;
  onDeleteCharacter?: (
    character: ExternalCharacterListItem,
  ) => Promise<boolean> | boolean;
  onRenameCharacter?: (
    character: ExternalCharacterListItem,
    nextCharacterName: string,
  ) => Promise<boolean> | boolean;
}

interface DeleteCharacterModalProps {
  character: ExternalCharacterListItem | null;
  deleteInProgress: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function DeleteCharacterModal({
  character,
  deleteInProgress,
  onOpenChange,
  onConfirm,
}: DeleteCharacterModalProps) {
  return (
    <Modal
      open={character !== null}
      onOpenChange={onOpenChange}
      title="Delete character?"
      description={
        character
          ? `Delete "${character.characterName}"? This cannot be undone.`
          : undefined
      }
      maxWidth={400}
    >
      <div className="flex flex-none justify-end gap-2 pt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={deleteInProgress}
        >
          Cancel
        </Button>
        <Button
          variant="primary-destructive"
          size="sm"
          onClick={onConfirm}
          disabled={deleteInProgress}
        >
          {deleteInProgress ? 'Deleting…' : 'Confirm'}
        </Button>
      </div>
    </Modal>
  );
}

export default function CharacterLibraryLoadModal({
  open,
  onOpenChange,
  apiKey,
  characters,
  selectedCharacterId,
  charactersLoading,
  charactersError,
  loadingCharacterId,
  onSelectCharacter,
  onDeleteCharacter,
  onRenameCharacter,
}: CharacterLibraryLoadModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [characterToDelete, setCharacterToDelete] =
    useState<ExternalCharacterListItem | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const resetModalState = useCallback(() => {
    setSearchQuery('');
    setIsEditMode(false);
    setCharacterToDelete(null);
    setDeleteInProgress(false);
  }, []);

  useEffect(() => {
    if (open) return;
    resetModalState();
  }, [open, resetModalState]);

  const handleLibraryOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        resetModalState();
      }
    },
    [onOpenChange, resetModalState],
  );

  const handleDeleteModalOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setCharacterToDelete(null);
    }
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!characterToDelete || !onDeleteCharacter) return;

    setDeleteInProgress(true);
    try {
      const deleted = await onDeleteCharacter(characterToDelete);
      if (deleted) {
        setCharacterToDelete(null);
      }
    } finally {
      setDeleteInProgress(false);
    }
  }, [characterToDelete, onDeleteCharacter]);

  const canManageCharacters =
    Boolean(onDeleteCharacter) || Boolean(onRenameCharacter);
  const showEditToggle =
    Boolean(apiKey) &&
    !charactersLoading &&
    !charactersError &&
    characters.length > 0 &&
    canManageCharacters;

  return (
    <>
      <Modal
        open={open}
        onOpenChange={handleLibraryOpenChange}
        title="Load character"
        description="Choose a custom character to load"
        maxWidth={1120}
      >
        <CartographerCharacterLibrary
          mode="modal"
          apiKey={apiKey}
          characters={characters}
          selectedCharacterId={selectedCharacterId}
          charactersLoading={charactersLoading}
          charactersError={charactersError}
          loadingCharacterId={loadingCharacterId}
          onSelectCharacter={onSelectCharacter}
          onDeleteCharacter={onDeleteCharacter ? setCharacterToDelete : undefined}
          onRenameCharacter={onRenameCharacter}
          toolbar={{
            showSearch: true,
            searchQuery,
            onSearchQueryChange: setSearchQuery,
            showEditToggle,
            editMode: isEditMode,
            onEditModeChange: setIsEditMode,
          }}
        />
      </Modal>
      <DeleteCharacterModal
        character={characterToDelete}
        deleteInProgress={deleteInProgress}
        onOpenChange={handleDeleteModalOpenChange}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
